import {
  Text,
  Html,
  Float,
  Environment,
  useGLTF,
  PresentationControls,
  ContactShadows /* useHelper */,
} from "@react-three/drei";
import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

// Full katakana + digits + Latin for a denser, more authentic charset
const MATRIX_CHARS =
  "ｦｧｨｩｪｫｬｭｮｯｰｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ" +
  "0123456789" +
  "ABCDEFGHIJKLMNOPQRSTUVWXYZ" +
  "ΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩαβγδεζ";
const FONT_SIZE = 18; // larger → crisper on 2048 canvas
const CANVAS_SIZE = 2048; // 4× resolution upgrade

function MatrixBackground() {
  const frameCount = useRef(0);
  const dropsRef = useRef([]); // current row per column
  const speedsRef = useRef([]); // ticks-per-drop per column (1=fast … 3=slow)
  const ticksRef = useRef([]); // per-column tick counter

  const texture = useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;

    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    const cols = Math.floor(CANVAS_SIZE / FONT_SIZE);
    dropsRef.current = Array.from({ length: cols }, () =>
      Math.floor(Math.random() * (CANVAS_SIZE / FONT_SIZE)),
    );
    // Mix of fast (1), medium (2) and slow (3) columns
    speedsRef.current = Array.from(
      { length: cols },
      () => Math.floor(Math.random() * 3) + 1,
    );
    ticksRef.current = Array.from({ length: cols }, () => 0);

    const tex = new THREE.CanvasTexture(canvas);
    tex.anisotropy = 4; // sharper at oblique angles
    tex.userData.canvas = canvas;
    return tex;
  }, []);

  useFrame(() => {
    // Throttle to ~30 fps
    frameCount.current++;
    if (frameCount.current % 2 !== 0) return;

    const canvas = texture.userData.canvas;
    const ctx = canvas.getContext("2d");
    const drops = dropsRef.current;
    const speeds = speedsRef.current;
    const ticks = ticksRef.current;
    const cols = drops.length;

    // Slightly slower fade → longer, more cinematic trails
    ctx.fillStyle = "rgba(0, 0, 0, 0.04)";
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    ctx.font = `bold ${FONT_SIZE}px monospace`;

    // Glow applied to every character drawn this frame
    ctx.shadowColor = "#00ff41";
    ctx.shadowBlur = 14;

    for (let i = 0; i < cols; i++) {
      const char =
        MATRIX_CHARS[Math.floor(Math.random() * MATRIX_CHARS.length)];
      const x = i * FONT_SIZE;
      const y = drops[i] * FONT_SIZE;

      // ~12 % chance of bright white head — rest are vivid green
      if (Math.random() > 0.88) {
        ctx.fillStyle = "#eeff00";
        ctx.fillText(char, x, y);
        // Second draw on top for an extra-bright pop
        ctx.fillStyle = "#ff0000";
        ctx.fillText(char, x, y);
      } else {
        ctx.fillStyle = "#00ff41";
        ctx.fillText(char, x, y);
      }

      // Advance this column only when its tick threshold is reached
      ticks[i]++;
      if (ticks[i] >= speeds[i]) {
        ticks[i] = 0;
        if (drops[i] * FONT_SIZE > CANVAS_SIZE && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    }

    ctx.shadowBlur = 0; // reset so the fade rect next frame isn't glowed
    texture.needsUpdate = true;
  });

  return (
    <mesh>
      <sphereGeometry args={[80, 64, 64]} />
      <meshBasicMaterial map={texture} side={THREE.BackSide} />
    </mesh>
  );
}

// const DEBUG_LIGHTS = true;
// const frontLightRef = useRef();
// const backLightRef  = useRef();
// const fillLightRef  = useRef();
// useHelper(DEBUG_LIGHTS && frontLightRef, THREE.PointLightHelper, 0.5, "#ffffff");
// useHelper(DEBUG_LIGHTS && backLightRef,  THREE.PointLightHelper, 0.5, "#ffffff");
// useHelper(DEBUG_LIGHTS && fillLightRef,  THREE.PointLightHelper, 0.3, "#aaaaaa");

const CLICK_THRESHOLD = 5; // px — movement beyond this is a drag, not a click

export default function Experience({ onZoomIn, camState, freeCamera }) {
  const computer = useGLTF("./retrogamingconsoletesting1.glb");
  const pointerDown = useRef({ x: 0, y: 0 });

  // Strip any lights baked into the GLB from Blender — collect first,
  // then remove, so we don't mutate the graph mid-traversal.
  useMemo(() => {
    const lights = [];
    computer.scene.traverse((obj) => {
      if (obj.isLight) lights.push(obj);
    });
    lights.forEach((obj) => obj.removeFromParent());
  }, [computer.scene]);

  return (
    <>
      <color args={["#000000"]} attach="background" />

      <MatrixBackground />

      {/* SoftShadows removed — it patches the shadow shader with random PCSS sampling,
          adding grain with no benefit since no lights have castShadow enabled. */}

      {/* Dark studio IBL — cool reflections on PBR surfaces without flooding the scene with light.
          "warehouse" is much dimmer than "apartment" so it stops over-brightening everything. */}
      <Environment preset="warehouse" background={false} />

      {/* ── Room lighting ──────────────────────────────────────────── */}
      {/* Warm overhead ceiling bulb — front of room, above desk */}
      <pointLight
        position={[0, 12, -3]}
        intensity={8}
        color="#ff9a40"
        decay={5}
        distance={28}
      />
      {/* Second ceiling bulb — back of room */}
      <pointLight
        position={[-7, 12, 11]}
        intensity={3}
        color="#ff9a40"
        decay={2.5}
        distance={28}
      />
      {/* Very soft fill — barely lifts the darkest shadows */}
      <pointLight
        position={[-4, 3, 4]}
        intensity={0.5}
        color="#ffc890"
        decay={2}
        distance={10}
      />
      {/* Matrix-green ambient — just enough to colour the deep shadows */}
      <ambientLight intensity={0.05} color="#061a06" />

      {/* PresentationControls gives drag-to-look + snap-back in normal mode.
           Disabled in free camera so OrbitControls has full authority. */}
      {freeCamera ? (
        <Float rotationIntensity={0.2}>
          <rectAreaLight
            width={2.5}
            height={1.65}
            intensity={0.35}
            color={"#a8d8ff"}
            rotation={[-0.1, Math.PI, 0]}
            position={[0, 0.55, -1.15]}
          />
          <primitive
            object={computer.scene}
            position={[0.1, -1.57, -1.62]}
            rotation={[-0.05, -0.01, 0.07]}
            scale={1.3}
            castShadow
            receiveShadow
          >
            <Html
              transform
              wrapperClass="htmlScreen"
              distanceFactor={0.3}
              position={[-0.19, 1.83, 0.95]}
              rotation={[-0.08, -0.38, -0.03]}
              scale={1.84}
            >
              <iframe src="https://minjin-chou-portfolio-os.vercel.app/" />
            </Html>
          </primitive>
        </Float>
      ) : (
        <PresentationControls
          global
          rotation={[0, 0, 0]}
          polar={[-0.4, 0.2]}
          azimuth={[-1, 0.75]}
          config={{ mass: 2, tension: 400 }}
          snap={{ mass: 4, tension: 400 }}
        >
          <Float rotationIntensity={0.2}>
            <rectAreaLight
              width={2.5}
              height={1.65}
              intensity={0.35}
              color={"#a8d8ff"}
              rotation={[-0.1, Math.PI, 0]}
              position={[0, 0.55, -1.15]}
            />
            <primitive
              object={computer.scene}
              position={[0.1, -1.57, -1.62]}
              rotation={[-0.05, -0.01, 0.07]}
              scale={1.3}
              castShadow
              receiveShadow
              onPointerDown={(e) => {
                pointerDown.current = {
                  x: e.nativeEvent.clientX,
                  y: e.nativeEvent.clientY,
                };
              }}
              onClick={(e) => {
                if (camState !== 0 || !onZoomIn) return;
                const dx = e.nativeEvent.clientX - pointerDown.current.x;
                const dy = e.nativeEvent.clientY - pointerDown.current.y;
                if (Math.sqrt(dx * dx + dy * dy) < CLICK_THRESHOLD) {
                  onZoomIn();
                }
              }}
              onPointerOver={
                camState === 0
                  ? () => (document.body.style.cursor = "pointer")
                  : undefined
              }
              onPointerOut={
                camState === 0
                  ? () => (document.body.style.cursor = "auto")
                  : undefined
              }
            >
              <Html
                transform
                wrapperClass="htmlScreen"
                distanceFactor={0.3}
                position={[-0.19, 1.83, 0.95]}
                rotation={[-0.08, -0.38, -0.03]}
                scale={1.84}
              >
                <iframe src="https://minjin-chou-portfolio-os.vercel.app/" />
              </Html>
            </primitive>
          </Float>
        </PresentationControls>
      )}
    </>
  );
}
